import { useCallback, useEffect, useState } from 'react';
import { fireAhaEvent, isDemoUser } from '@/lib/analytics';

// useFireOncePerUser — guards a magic-moment / unlock-milestone event so it
// fires at most once per user lifetime.
//
// Storage strategy:
//   - Real users:  localStorage (persists across tabs / sessions / devices via
//                  BE merge in v0.5).
//   - DEMO_AUTO_USER=1: sessionStorage (per-tab; resets between demos so the
//                                       UX dramatic moment can be replayed).
//
// Telemetry: fires the event named `eventName` exactly once. Subsequent fires
// are no-ops. PII rules from telemetry_events.aha.v1.yaml are honored upstream
// in fireAhaEvent.
export interface UseFireOncePerUserResult {
  /** Whether the moment has already fired (persisted across mounts). */
  fired: boolean;
  /** Idempotently fire the moment. Safe to call from effects. */
  fire: (props?: Record<string, unknown>) => void;
}

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return isDemoUser() ? window.sessionStorage : window.localStorage;
}

export function useFireOncePerUser(
  eventName: string,
  storageKey: string,
): UseFireOncePerUserResult {
  const [fired, setFired] = useState<boolean>(() => {
    const s = storage();
    if (!s) return false;
    try {
      return s.getItem(storageKey) !== null;
    } catch {
      return false;
    }
  });

  // Re-check on mount in case storage was mutated by another tab between
  // initial state computation and effect.
  useEffect(() => {
    const s = storage();
    if (!s) return;
    try {
      if (s.getItem(storageKey) !== null && !fired) setFired(true);
    } catch {
      /* ignore */
    }
  }, [storageKey, fired]);

  const fire = useCallback(
    (props?: Record<string, unknown>) => {
      if (fired) return;
      const s = storage();
      try {
        if (s) s.setItem(storageKey, new Date().toISOString());
      } catch {
        /* storage may be full or disabled; non-fatal */
      }
      setFired(true);
      fireAhaEvent(eventName, props ?? {});
    },
    [fired, eventName, storageKey],
  );

  return { fired, fire };
}
